import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { l_chevron_right } from '../../../icons';
import getRenderable from '../../../lib/getRenderable';
import Icon from '../Icon';
import { BASE_CLASSES, ANIMATION_STEP_AMOUNT } from './constants';
import './index.scss';

class Collapsible extends React.Component {
    constructor(props) {
        super(props);
        const expanded = props.defaultExpanded || props.expanded;

        this.state = {
            expanded,
            fullyExpanded: expanded,
            dirty: false
        };

        this.element = null;
        this.expandInterval = null;
        this.controlled = typeof props.expanded !== 'undefined';

        this.expand = this.expand.bind(this);
        this.minimize = this.minimize.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleExpandedChange = this.handleExpandedChange.bind(this);
    }

    UNSAFE_componentWillReceiveProps({ expanded }) {
        const { expanded: currentExpanded } = this.state;

        if (typeof expanded === 'undefined' || expanded === currentExpanded) { return; }

        // We pass the inverse value so the UI updates according to this state
        this.handleExpandedChange(!expanded);
    }

    get increment() {
        return Math.ceil(this.element.scrollHeight / ANIMATION_STEP_AMOUNT);
    }

    get contentProps() {
        const {
            dirty,
            fullyExpanded
        } = this.state;

        const props = {
            className: 'fit-collapsible-content',
            ref: (element) => this.element = element
        };

        if (!dirty && !fullyExpanded) {
            props.style = {
                height: 0
            };
        }

        return props;
    }

    handleExpandedChange(expanded) {
        expanded ? this.minimize() : this.expand();
    }

    setDirty() {
        this.setState({ dirty: true });
    }

    /**
     * Expands the panel.
     */
    expand() {
        const { element } = this;

        const frame = () => {
            const elementHeight = element.offsetHeight;
            const shouldStopExpanding = elementHeight >= element.scrollHeight;

            if (shouldStopExpanding) {
                clearInterval(this.expandInterval);
                element.style.height = null;
                this.setState({ fullyExpanded: true });
                this.setDirty();
            } else {
                const newHeight = Math.min(element.scrollHeight, elementHeight + this.increment);

                requestAnimationFrame(() => {
                    element.style.height = `${newHeight}px`;
                });
            }
        };

        clearInterval(this.expandInterval);
        this.expandInterval = setInterval(frame, 1);
        this.setState({ expanded: true });
        this.props.onExpand();
    }

    /**
     * Minimizes the panel.
     */
    minimize() {
        const { element } = this;

        const frame = () => {
            const elementHeight = element.offsetHeight;
            const shouldStopMinimizing = elementHeight <= 0;

            if (shouldStopMinimizing) {
                clearInterval(this.expandInterval);
                this.setState({ expanded: false });
            } else {
                const newHeight = Math.max(0, elementHeight - this.increment);

                requestAnimationFrame(() => {
                    element.style.height = `${newHeight}px`;
                });
            }
        };

        clearInterval(this.expandInterval);
        this.expandInterval = setInterval(frame, 1);
        this.setState({ fullyExpanded: false });
        this.props.onMinimize();
        this.setDirty();
    }

    /**
     * Toggles the panel expanded state.
     */
    toggle() {
        const {
            disabled,
            onToggle
        } = this.props;
        const { expanded } = this.state;

        if (disabled) { return; }

        onToggle(!expanded);

        if (this.controlled) { return; }

        this.handleExpandedChange(expanded);
    }

    render() {
        const {
            children,
            title,
            className,
            disabled
        } = this.props;
        const {
            expanded,
            fullyExpanded
        } = this.state;

        const classes = classnames(BASE_CLASSES, className, {
            'fit-collapsible-expanded': expanded,
            'fit-collapsible-fully-expanded': fullyExpanded
        });
        const titleClasses = classnames('fit-collapsible-title', {
            'fit-collapsible-title-string': typeof title === 'string'
        });

        return (
            <article className={classes}
                disabled={disabled}>
                <div className="fit-collapsible-title-wrapper"
                    onClick={this.toggle}>
                    <div className={titleClasses}>{ getRenderable(title) }</div>
                    <div className="fit-collapsible-chevron-wrapper">
                        <Icon className="fit-collapsible-chevron"
                            size={14}>
                            {l_chevron_right}
                        </Icon>
                    </div>
                </div>
                <div {...this.contentProps}>
                    { getRenderable(children) }
                </div>
            </article>
        );
    }
}

Collapsible.propTypes = {
    /**
     * The `<Collapsible>`'s content.
     */
    children: PropTypes.node.isRequired,
    /**
     * The `<Collapsible>`'s title. If a `String` is passed, some default styling will be applied.
     */
    title: PropTypes.node.isRequired,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Whether or not the panel is disabled.
     * A disabled panel is not interactable.
     */
    disabled: PropTypes.bool,
    /**
     * The panel's initial state.
     */
    defaultExpanded: PropTypes.bool,
    /**
     * Controls the `<Collapsible>`'s state programatically.
     * This will make the component fully controlled.
     */
    expanded: PropTypes.bool,
    /**
     * A callback function called when the panel is toggled.
     * It receives one `Boolean` argument - whther the `<Collapsible>` is toggled open or not.
     */
    onToggle: PropTypes.func,
    /**
     * A callback function called when the panel is expanded.
     */
    onExpand: PropTypes.func,
    /**
     * A callback function called when the panel is minimized.
     */
    onMinimize: PropTypes.func
};

Collapsible.defaultProps = {
    disabled: false,
    onToggle: () => null,
    onExpand: () => null,
    onMinimize: () => null
};

export default Collapsible;
